import React, { useEffect, useState } from 'react';
import { Zap, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '@hooks/useChat.ts';
import { ChatHistoryMenu } from '../history/ChatHistoryMenu';
import { ShareButton } from '../chat/ShareButton';
import { loadAssistants } from '@config/assistants.config.ts';
import { StorageService } from '@services/storage/StorageService.ts';
import { Assistant } from '@/types';
import DriveSourceLogo from '@/assets/DriveSource Logo White.svg';
import HomeSourceLogo from '@/assets/HomeSource Logo White.svg';
import { renderAssistantIcon } from '@/utils/RenderAssistantIcon';
import { getIconColorFromAssistant } from '@/utils/colorUtils';

const getHeaderLogo = (): { src: string; alt: string } => {
    const displayName = import.meta.env.VITE_ASSISTANT_DISPLAY_NAME;
    if (displayName === 'DriveSource') {
        return { src: DriveSourceLogo, alt: 'DriveSource Logo' };
    }
    if (displayName === 'HomeSource') {
        return { src: HomeSourceLogo, alt: 'HomeSource Logo' };
    }
    return { src: DriveSourceLogo, alt: 'Logo' };
};

export const Header: React.FC = () => {
    const { selectedAssistant, setSelectedAssistant, startNewChat, messages } = useChat();
    const navigate = useNavigate();
    const [assistants, setAssistants] = useState<Record<string, Assistant>>({});
    const [loadingAssistants, setLoadingAssistants] = useState(true);
    const storageService = StorageService.getInstance();
    const [userStreak, setUserStreak] = useState(0);

    useEffect(() => {
        const fetchAssistants = async () => {
            try {
                const loadedAssistants = await loadAssistants();
                setAssistants(loadedAssistants);
            } catch (error) {
                console.error('Failed to load assistants:', error);
            } finally {
                setLoadingAssistants(false);
            }
        };
        fetchAssistants();
    }, []);

    const assistant = selectedAssistant ? assistants[selectedAssistant] : null;


    const handleLogoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        // Navigate to home and start a new chat
        navigate('/');
        startNewChat();
    };

    const renderCenterContent = () => {
        if (loadingAssistants || !assistant) return null;
        const iconColor = getIconColorFromAssistant(assistant.bgGradient, assistant.accentColor);
        return (
            <div className="hidden sm:flex absolute left-1/2 -translate-x-1/2 items-center gap-2.5">
                <div className={`p-2 rounded-full bg-gradient-to-br ${assistant.bgGradient}`}>
                    {renderAssistantIcon({ assistant, iconColor, size: 'md' })}
                </div>
                {import.meta.env.VITE_WELCOME_TAGLINE && (
                    <p className="text-sm sm:text-base font-medium text-white whitespace-nowrap">
                        {import.meta.env.VITE_WELCOME_TAGLINE}
                    </p>
                )}
            </div>
        );
    };

    useEffect(() => {
        const today = new Date().toDateString();
        const lastVisit = storageService.getLastVisit();
        const savedStreak = storageService.getUserStreak();

        if (lastVisit === today) {
            setUserStreak(savedStreak);
        } else if (lastVisit) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            if (lastVisit === yesterday.toDateString()) {
                const newStreak = savedStreak + 1;
                setUserStreak(newStreak);
                storageService.updateUserStreak(newStreak);
            } else {
                setUserStreak(1);
                storageService.updateUserStreak(1);
            }
        } else {
            setUserStreak(1);
            storageService.updateUserStreak(1);
        }
        storageService.updateLastVisit(today);
    }, []);

    return (
        <div className="fixed top-0 left-0 right-0 bg-[#1a1a1a] border-b border-gray-600 z-10">
            <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-2 sm:py-3 md:py-4">
                <div className="flex items-center justify-between w-full relative">
                    {/* Left section - Logo */}
                    <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
                        <a
                            href="#"
                            onClick={handleLogoClick}
                            className="block transition-opacity hover:opacity-80"
                        >
                            <img
                                src={getHeaderLogo().src}
                                alt={getHeaderLogo().alt}
                                className="h-6 sm:h-8 md:h-9 lg:h-10 w-auto cursor-pointer"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        </a>
                    </div>

                    {/* Center content - hidden on mobile */}
                    {renderCenterContent()}

                    {/* Right section */}
                    <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
                        {userStreak > 1 && (
                            <div className="hidden lg:flex items-center gap-1 px-3 py-1 bg-amber-100 rounded-full">
                                <Zap className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">
                                    {userStreak} day streak!
                                </span>
                            </div>
                        )}

                        {/* Share Button */}
                        {assistant && (
                            <ShareButton messages={messages} assistant={assistant} />
                        )}


                        {/* Chat Now — always visible */}
                        <button
                            onClick={handleLogoClick}
                            className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-full transition-all duration-300 ease-in-out transform hover:scale-105"
                        >
                            <MessageCircle className="size-3 sm:size-4" />
                            <span className="text-xs sm:text-sm">Chat Now</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};